import React, { useState, useEffect, useRef } from "react";
import PrescriptionService from "services/prescription.service";
import AuthService from "services/auth.service";
import { Form, Modal, Button } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import IconRemove from "assets/icons/Remove.svg";
import IconPlus from "assets/icons/Plus Circle.svg";
import MedicineDetailsModal from "./MedicineDetailsModal";
import MedicinesConfirmModal from "./MedicinesConfirmModal";
import PrescriptionDetails from "./PrescriptionDetails";
import "react-day-picker/lib/style.css";

function Prescription({ chat }) {
  const defaultMedicine = {
    medicine_id: null,
    quantity: "",
    unit_price: "",
  };
  const medLabel = {
    consume_before_meal: "Before Meal",
    consume_after_meal: "After Meal",
    consume_when_necessary: "When Necessary",
    consumption_to_complete: "To Complete",
    consumption_cause_drowsiness: "Cause Drowsiness",
  };
  const defaultMedicineDetails = {
    consumption_quantity: "",
    consumption_unit: "",
    consumption_per_day: "",
    consumption_days: "",
    consume_before_meal: false,
    consume_after_meal: false,
    consume_when_necessary: false,
    consumption_to_complete: false,
    consumption_cause_drowsiness: false,
    remarks: "",
  };
  const isPrescriptionCancelled = useRef(false);
  const isPrescribedCancelled = useRef(false);
  const [prescribed, setPrescribed] = useState(
    chat?.e_prescription?.prescription_id
  );
  const e_delivery = chat.e_prescription.e_delivery;
  const [noPrescription, setNoPrescription] = useState(
    !chat.e_prescription.enabled
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [medicines, setMedicines] = useState([defaultMedicine]);
  const [showMedicineDetail, setShowMedicineDetail] = useState(false);
  const [currentMedicine, setCurrentMedicine] = useState({});
  const [currentMedicineDetails, setCurrentMedicineDetails] = useState(
    defaultMedicineDetails
  );
  const [showMedicinesConfirm, setShowMedicinesConfirm] = useState(false);
  const [erx, setErx] = useState({});

  const PrescriptionConfirmationModal = () => {
    return (
      <Modal
        show={showConfirmation}
        onHide={() => {
          setShowConfirmation(false);
        }}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body>
          By checking <b>"No prescriptions needed for this consultation"</b>,
          you are indicating that prescription is not required for this
          consultation. Do note that this action is irreversible. Please
          confirm.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowConfirmation(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmNoPrescription}>
            Yes, Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  useEffect(() => {
    if (!prescribed) {
      const params = {
        e_prescription: !noPrescription,
      };
      PrescriptionService.requirePrescription(chat.id, params).then(
        (data) => {
          // after presciption required updated
        },
        (error) => {
          if (error.response.status === 401) {
            AuthService.logout();
            window.location.replace("/eb/login");
          }
        }
      );
    }
    return () => {
      isPrescriptionCancelled.current = true;
    };
  }, [noPrescription]); // eslint-disable-line

  useEffect(() => {
    if (prescribed) {
      PrescriptionService.getPrescription(prescribed).then(
        (data) => {
          setErx(data.prescription);
        },
        (error) => {
          if (error.response.status === 401) {
            AuthService.logout();
            window.location.replace("/eb/login");
          }
        }
      );
    }
    return () => {
      isPrescribedCancelled.current = true;
    };
  }, [prescribed]); // eslint-disable-line

  const confirmNoPrescription = () => {
    setNoPrescription(true);
    setShowConfirmation(false);
  };

  const handleSetNoPrescription = (e) => {
    if (e.target.checked) {
      setShowConfirmation(true);
    } else {
      setNoPrescription(false);
    }
  };

  // prescriptions
  const prescriptions = () => {
    const handleMedChange = (e, index) => {
      const newState = medicines.map((obj, i) => {
        if (i === index) {
          return { ...obj, [e.target.name]: e.target.value };
        }
        return obj;
      });

      setMedicines(newState);
    };

    const removeMedicine = (index) => {
      setMedicines((prevState) => {
        return prevState.filter((el, i) => i !== index);
      });
    };

    const loadMedicines = (query) => {
      if (query.length === 0) {
        return [];
      }
      const params = {
        query: query,
      };

      return PrescriptionService.medicines(params).then(
        (data) => {
          return data.result.map((d) => {
            return {
              value: d.id,
              label: d.name,
              dosage: d.dosage,
              dosage_form: d.dosage_form,
            };
          });
        },
        (error) => {
          if (error.response.status === 401) {
            AuthService.logout();
            window.location.replace("/eb/login");
          }
        }
      );
    };

    const onMedicineChange = (med, index) => {
      const newState = medicines.map((obj, i) => {
        if (i === index) {
          return { ...obj, medicine_id: null };
        }
        return obj;
      });
      setMedicines(newState);
      setCurrentMedicine({ index: index, med: med });
      setCurrentMedicineDetails(defaultMedicineDetails);
      setShowMedicineDetail(true);
    };

    return (
      <div>
        <div className="d-flex medicine-row">
          <div className="col-12 col-lg-6 font-weight-bold text-uppercase">
            Medicine Name
          </div>
          <div className="col-12 col-lg-2 font-weight-bold text-uppercase">
            Quantity
          </div>
          <div className="col-12 col-lg-2 font-weight-bold text-uppercase">
            Unit Price
          </div>
        </div>
        {medicines.map((medicine, index) => {
          return (
            <div className="d-flex medicine-row" key={`medicine_${index}`}>
              <AsyncSelect
                className="react-select med-field p-0 col-12 col-lg-6"
                placeholder="Medicine Name"
                cacheOptions
                loadOptions={loadMedicines}
                onChange={(e) => onMedicineChange(e, index)}
                value={medicine.medicine_id ? currentMedicine.med : null}
                formatOptionLabel={(option, { context }) => {
                  return context === "menu"
                    ? option.label
                    : selectedMedicineLabel(medicine);
                }}
              />
              <Form.Control
                className="med-field col-12 col-lg-2"
                type="number"
                name="quantity"
                placeholder="0"
                onChange={(e) => {
                  handleMedChange(e, index);
                }}
                min={1}
                value={medicine.quantity}
              />
              <Form.Control
                className="med-field col-12 col-lg-2"
                type="number"
                name="unit_price"
                placeholder="RM 0.00"
                onChange={(e) => {
                  handleMedChange(e, index);
                }}
                min={0.01}
                value={medicine.unit_price}
                disabled={!e_delivery}
              />
              <div className="col-12 col-lg-1 px-1 text-nowrap text-center">
                RM{" "}
                {(medicine.quantity * medicine.unit_price).toFixed(2) || "0.00"}
              </div>
              {medicines.length > 1 && (
                <button
                  className="btn btn-link col-12 col-lg-1"
                  onClick={() => {
                    removeMedicine(index);
                  }}
                >
                  <img src={IconRemove} alt="delete-icon" />
                </button>
              )}
            </div>
          );
        })}
        <button
          className="btn btn-link text-danger add-med-btn"
          onClick={addMedicine}
        >
          <img src={IconPlus} alt="plus-icon" className="plus-icon" />
          Add An Item
        </button>
        <hr />
        <div className="d-flex medicine-row">
          <div className="col-lg-10"></div>
          <div className="col-lg-1 px-1 ml-5 text-nowrap text-center">
            RM {getTotal(medicines)}
          </div>
          <div className="col-lg-1"></div>
        </div>
        <hr />
      </div>
    );
  };

  const selectedMedicineLabel = (med) => {
    return (
      <div className="selected-med">
        <div className="med-label">
          {med.medicine?.label || med.medicine?.name}
        </div>
        <small>
          {[
            `(${med.consumption_quantity}) ${med.consumption_unit}, (${med.consumption_per_day}) Times Daily, (${med.consumption_days}) Days`,
            Object.values(
              Object.fromEntries(
                Object.entries(medLabel).filter(([key]) => med[key])
              )
            ).join(", "),
            med.remarks,
          ]
            .filter(Boolean)
            .join(", ")}
        </small>
      </div>
    );
  };

  const getTotal = (_tempMed) => {
    var total = 0;
    _tempMed?.forEach((m) => {
      total += m.quantity * m.unit_price || 0;
    });

    return total.toFixed(2) || "0.00";
  };

  const addMedicine = () => {
    setMedicines((prevState) => {
      return [...prevState, defaultMedicine];
    });
  };

  const updateMedicineDetails = () => {
    const newState = medicines.map((obj, i) => {
      if (i === currentMedicine.index) {
        return {
          ...obj,
          medicine_id: currentMedicine.med.value,
          medicine: currentMedicine.med,
          ...currentMedicineDetails,
        };
      }
      return obj;
    });
    setMedicines(newState);
    setShowMedicineDetail(false);
  };

  // address
  const PickUpLocation = () => {
    const pickUpLocation = prescribed
      ? erx.pickup_location
      : AuthService.getCurrentUser().has_multi_clinics
      ? false
      : AuthService.getCurrentUser().pickup_location;
    if (pickUpLocation) {
      var pickUpLocationAddress = "Not Applicable";
      if (pickUpLocation?.name) {
        pickUpLocationAddress = pickUpLocation?.name;
      } else if (pickUpLocation.practice_location_id || e_delivery) {
        pickUpLocationAddress = pickUpLocation.address.split(",").map((a) => {
          return <div key={a}>{a}</div>;
        });
      }
      return (
        <div className="mt-6 pick-up-location">
          <div className="title">Pick-Up Location</div>
          <div>{pickUpLocationAddress}</div>
        </div>
      );
    }

    return null;
  };

  // medicines confirmation
  const medicinesConfirm = () => {
    // check if everything not null
    var error = false;
    const excludeKeys = ["remarks"];

    if (!e_delivery) {
      excludeKeys.push("unit_price");
    }
    medicines.forEach((med) => {
      Object.keys(med).forEach((key, index) => {
        if (
          !excludeKeys.includes(key) &&
          (med[key] === null || med[key] === "" || med[key] === 0)
        ) {
          error = true;
          return;
        }
      });
      if (error) return;
    });

    if (error) {
      alert("Please complete the prescription before submitting.");
    } else {
      setShowMedicinesConfirm(true);
    }
  };

  return (
    <div className="prescription">
      <h6 className="p-2">Prescription</h6>
      {!chat?.e_prescription?.within_distance && (
        <div className="not-within-distance">
          <i>
            ** This patient delivery address is further than 20km or system
            defined distance, delivery cost might be high or not possible to
            deliver
          </i>
        </div>
      )}
      {prescribed ? (
        <PrescriptionDetails
          medicines={erx?.prescription_medicines}
          medicineLabel={selectedMedicineLabel}
          getTotal={getTotal}
          PickUpLocation={PickUpLocation}
          erx={erx}
        />
      ) : (
        <>
          <Form.Check inline>
            <Form.Check.Input
              id="prescription"
              type="checkbox"
              checked={noPrescription}
              onChange={(e) => {
                handleSetNoPrescription(e);
              }}
            />
            <Form.Check.Label htmlFor="prescription">
              No prescription needed for this consultation
            </Form.Check.Label>
          </Form.Check>
          {!noPrescription && (
            <>
              {!e_delivery && (
                <div className="mt-3 font-italic">
                  <small>
                    Price is not needed as patient's delivery address exceed
                    Grab's coverage.
                  </small>
                </div>
              )}
              {prescriptions()}
              <div className="d-flex justify-content-between align-items-end">
                <PickUpLocation />
                <div>
                  <button
                    className="btn save-prescription-btn"
                    onClick={() => medicinesConfirm()}
                  >
                    Save & Send
                  </button>
                </div>
              </div>
            </>
          )}
          <PrescriptionConfirmationModal />
          <MedicineDetailsModal
            setShowMedicineDetail={setShowMedicineDetail}
            showMedicineDetail={showMedicineDetail}
            currentMedicine={currentMedicine}
            medLabel={medLabel}
            currentMedicineDetails={currentMedicineDetails}
            setCurrentMedicineDetails={setCurrentMedicineDetails}
            updateMedicineDetails={updateMedicineDetails}
          />
          <MedicinesConfirmModal
            setShowMedicinesConfirm={setShowMedicinesConfirm}
            showMedicinesConfirm={showMedicinesConfirm}
            medicines={medicines}
            medicineLabel={selectedMedicineLabel}
            getTotal={getTotal}
            PickUpLocation={PickUpLocation}
            chat_id={chat.id}
            setPrescribed={setPrescribed}
          />
        </>
      )}
    </div>
  );
}

export default Prescription;
